/**
 * Casekaro — front-end tracking
 * v5  (2026-08-25)
 *
 * WHAT CHANGED FROM v4 — and why
 * ------------------------------
 * On 25 Aug the Model Request sheet hit Google's 10,000,000-cell workbook cap
 * and stopped accepting writes for the second time in four days. 598,384 of its
 * 953,081 rows — 60.6%, and up to 89% of the most recent weeks — were never
 * phone data. Two causes, both of them this file:
 *
 *   1. This script only skipped iPhone/iPad/iPod. Every desktop browser fell
 *      through to fromUA(), which happily returned "Windows NT 10.0" or
 *      "Macintosh" from the UA string and posted it as a phone model.
 *      165,313 Macintosh rows + 133,684 Windows rows = 31% of the sheet.
 *
 *   2. Crawlers re-send on every single crawl. ck_sent lives in localStorage,
 *      which bots do not keep, so the "one row per browser, ever" guard never
 *      applied to them. One UA — Googlebot-mobile's "Nexus 5X" — was 238,396
 *      rows on its own, a full quarter of the sheet.
 *
 * Both are now refused before the request is built. The backend (v11, Version
 * 41) also drops them, so this is not about data correctness any more — it is
 * about not making ~60% of the requests in the first place.
 *
 *   3. window.ckTrack no longer posts. The backend has discarded every popup
 *      event since v10 (`return reply('OK ignored')`) — nothing consumes them
 *      and nothing reaches the Debug sheet. It was posting roughly 107,000
 *      requests a day into a function whose only job was to ignore them. The
 *      hook stays defined so callers that do `if (window.ckTrack)` still work.
 *
 * TEST ON A REAL ANDROID PHONE BEFORE CONSIDERING THIS DONE. If the Android
 * check is wrong you get zero detections and nothing will tell you for hours.
 *
 * WHY mode:'no-cors' STAYS
 * ------------------------
 * Tested from casekaro.com on 2026-08-01. A readable (CORS) POST to the
 * Apps Script endpoint returns 404 for any browser signed into more than one
 * Google account — Google rewrites the post-redirect URL to /macros/u/1/s/...
 * which does not resolve. The write itself still lands (doPost runs on the
 * initial POST; only the redirect target 404s), so a readable-response build
 * would see a "failure", retry 3x, write duplicate rows, and never set
 * ck_sent. That is strictly worse than firing blind.
 *
 * So we stay on no-cors and treat "the request resolved" as success. That
 * catches offline / DNS / blocked-request failures, which is the common case,
 * and is still a real improvement on setting the flag synchronously.
 *
 * It also means this file can never read the backend's reply — including
 * v11's "OK not-a-phone". Filtering has to happen here or not at all.
 *
 * Duplicate safety: flushBuffer() on the backend collapses repeat device
 * pings by session ID, so a retry can never produce two rows.
 */
(function () {
  'use strict';

  var ENDPOINT = 'https://script.google.com/macros/s/AKfycbwXiQCucz0OEoIopRf6cj1W27xm3we0NXZ_H_uW7c9edD-JKW4MGHAtA-FzHh1a6pbN_w/exec';

  /* ---- session id -------------------------------------------------- */

  var sid = localStorage.getItem('ck_session_id');
  if (!sid) {
    sid = 'ck_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('ck_session_id', sid);
  }

  /* ---- attach session to cart (once per tab session) ---------------- */
  /* Runs for every visitor, desktop included — the cart attribute is what
     joins an order back to a session, and desktop visitors place orders. */

  if (!sessionStorage.getItem('ck_cart_tagged')) {
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { ck_session_id: sid } })
    }).then(function () {
      sessionStorage.setItem('ck_cart_tagged', '1');
    })['catch'](function () { /* cart not ready — next page load retries */ });
  }

  /* ---- transport ---------------------------------------------------- */
  /* text/plain is a CORS-safelisted content type, so it survives no-cors
     intact. Apps Script reads e.postData.contents either way. */

  function post(payload) {
    return fetch(ENDPOINT, {
      method: 'POST',
      mode: 'no-cors',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify(payload)
    });
  }

  function postWithRetry(payload, tries) {
    return post(payload)['catch'](function (err) {
      if (tries <= 1) throw err;
      return new Promise(function (res) {
        setTimeout(res, 1500 + Math.random() * 2500);   // jittered
      }).then(function () {
        return postWithRetry(payload, tries - 1);
      });
    });
  }

  /* ---- popup event hook --------------------------------------------- */
  /* Kept defined so callers guarding with `if (window.ckTrack)` keep working,
     but deliberately does nothing.
   *
   * The backend has discarded every event since v10 — doPost hits
   * `return reply('OK ignored')` for anything with an `event` key. Sheet1
   * columns G and H were retired, the Events sheet was deleted, and nothing
   * reads them. Posting was ~107,000 requests a day for no result.
   *
   * Popup metrics (mp_pill_shown, mp_pill_cta, ...) go to fbq / gtag from
   * ck-device-popup.js — that path is unaffected by this.
   *
   * To restore posting, put back:
   *   postWithRetry({ event: eventName, session_id: s }, 2)['catch'](function () {});
   * and give doPost an `event` branch that actually stores something. Both
   * ends have to change together; one without the other does nothing. */

  window.ckTrack = function (eventName) { /* intentionally a no-op — see above */ };

  /* ---- device detection --------------------------------------------- */

  if (localStorage.getItem('ck_sent')) return;          // already recorded, ever
  if (sessionStorage.getItem('ck_attempted')) return;   // one chain per tab
  if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) return;

  /* Sheet1 records Android phone models; iPhones go through the picker.
     Anything that is not an Android phone was never wanted here — see note 1
     in the header. Checked before ck_attempted is set, so a desktop visitor
     just returns immediately on every page load and costs nothing. */
  if (!/Android/i.test(navigator.userAgent)) return;

  /* Crawlers declare themselves and do not persist localStorage, so ck_sent
     never stops them — see note 2. None of these words appear in a real
     Android device UA, which looks like:
       Mozilla/5.0 (Linux; Android 14; SM-S928B) AppleWebKit/537.36 ... */
  if (/bot|crawl|spider|slurp|headless/i.test(navigator.userAgent)) return;

  sessionStorage.setItem('ck_attempted', '1');

  function fromUA() {
    var ua = navigator.userAgent;
    var m = ua.match(/;\s*([^;]+?)\s+Build\//i) || ua.match(/\(([^;)]+);/);
    return m ? m[1].trim() : 'Unknown';
  }

  function send(model) {
    postWithRetry({
      model: model || fromUA(),
      ua: navigator.userAgent,
      referrer: document.referrer || 'direct',
      session_id: sid
    }, 3)
      .then(function () {
        localStorage.setItem('ck_sent', '1');           // only once it resolved
      })
      ['catch'](function () {
        // Flag stays unset — a later tab session tries again instead of
        // losing this visitor permanently.
      });
  }

  if (navigator.userAgentData && navigator.userAgentData.getHighEntropyValues) {
    navigator.userAgentData.getHighEntropyValues(['model'])
      .then(function (u) { send(u.model); })
      ['catch'](function () { send(''); });
  } else {
    send('');
  }
})();